import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  IconButton,
  Typography,
  Box,
  Grid
} from "@mui/material";
import { Form, Spinner, Image } from "react-bootstrap";
import { QuestionApiServiceImpl } from "app/services/Ananse/impl/QuestionApiService";
import CloseIcon from "@mui/icons-material/Close";
import { AnswerType, FileType } from "app/types";
import { useTranslation } from "_metronic/i18n/language";

interface ImageUploadProps {
  answers: AnswerType[];
  answersOld: AnswerType[];
  open: boolean;
  onClose: () => void;
}

export const getFilesByFileOwnerId = async (
  fileOwnerId: string
): Promise<FileType | undefined> => {
  try {
    const response = await questionService.getFileByFileOwnerId(fileOwnerId);
    return response[0];
  } catch (error) {
    // console.log("geFilesByFileOwnerId: ", error);
  }
};

const questionService = new QuestionApiServiceImpl();

const ModalAddImageAnswer: React.FC<ImageUploadProps> = ({
  answers,
  answersOld,
  open,
  onClose
}) => {
  const translate = useTranslation()
  const [files, setFiles] = useState<{
    [x: number]: FileType;
  }>({});
  const [previews, setPreviews] = useState<{ [key: number]: string | null }>(
    {}
  );
  const [uploadProgress, setUploadProgress] = useState<{
    [key: number]: number;
  }>({});  const [isUploading, setIsUploading] = useState<{[key: number]: boolean}>({});
  const [overallProgress, setOverallProgress] = useState<number>(0);
  const [hasNewImages, setHasNewImages] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleClose = () => {
    setIsUploading({});
    setFiles({});
    setPreviews({});
    setUploadProgress({});
    setOverallProgress(0);
    setHasNewImages(false);
    setIsSubmitting(false);
    onClose();
  };


  const handleFileChange = (
    event: React.ChangeEvent<any>,
    quizQuestionAnswerId: number
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const prepareData: FileType = {
        file
      };

      // Clean previous preview URL if exists to avoid memory leaks
      if (previews[quizQuestionAnswerId]) {
        URL.revokeObjectURL(previews[quizQuestionAnswerId] || '');
      }

      setFiles(prev => ({ ...prev, [quizQuestionAnswerId]: prepareData }));
      setPreviews(prev => ({
        ...prev,
        [quizQuestionAnswerId]: URL.createObjectURL(file)
      }));
      setHasNewImages(true);
    }
  };
  const calculateOverallProgress = (progress: { [key: number]: number }) => {
    const answersWithFiles = answers.filter(answer => files[answer.quizQuestionAnswerId]);
    const total = answersWithFiles.length;

    if (total === 0) {
      setOverallProgress(0);
      return;
    }

    const sumProgress = Object.values(progress).reduce(
      (sum, value) => sum + value,
      0
    );
    setOverallProgress(Math.round(sumProgress / total));
  };
  const handleSubmit = async (event: React.FormEvent) => {
    try {
      event.preventDefault();
      setIsSubmitting(true);
      setUploadProgress({});
      setOverallProgress(0);

      const answersWithFiles = answers.filter(answer => files[answer.quizQuestionAnswerId]);
      if (answersWithFiles.length === 0) {
        return;
      }

      for (const answer of answersWithFiles) {
        const file = files[answer.quizQuestionAnswerId];

        // Se não houver novo arquivo selecionado, pular o envio
        if (!file) {
          continue;
        }

        const fileOwnerId = answer.quizQuestionAnswerId.toString();
        const formData = new FormData();

        if (file.file) {
          formData.append("file", file.file);
        } else if (file.fileId && file.tempUri) {
          formData.append("fileId", file.fileId.toString());
          formData.append("tempUri", file.tempUri);
          formData.append("path", file.path || "");
          formData.append("downloads", file.downloads?.toString() || "0");
        }

        formData.append("fileOwnerId", fileOwnerId);

        setIsUploading(prev => ({
          ...prev,
          [answer.quizQuestionAnswerId]: true
        }));

        try {
          // Método para cadastrar o arquivo
          const reqBase = { method: "POST", url: "questionanswerfile" };

          await questionService.makeHttpRequest({
            ...reqBase,
            data: formData,
            onUploadProgress: (progressEvent: ProgressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(prev => {
                const updatedProgress = {
                  ...prev,
                  [answer.quizQuestionAnswerId]: progress
                };
                calculateOverallProgress(updatedProgress);
                return updatedProgress;
              });
            }
          });

          console.log(
            `File uploaded successfully for answer ${answer.quizQuestionAnswerId}`
          );
        } catch (error) {
          console.error(
            `Error uploading file for answer ${answer.quizQuestionAnswerId}:`,
            error
          );
        } finally {
          setIsUploading(prev => {
            const updated = { ...prev };
            delete updated[answer.quizQuestionAnswerId];
            return updated;
          });
        }
      }
    } catch (error) {
      console.error("Error during file upload:", error);
    } finally {
      // Após o envio, redefinir o estado de novas imagens
      setHasNewImages(false);
      setUploadProgress({});
      setOverallProgress(0);
      setIsSubmitting(false);
      onClose();
    }
  };

  useEffect(() => {
    const fetchExistingImages = async () => {
      const previewsMap: { [key: number]: string | null } = {};
      const filesMap: { [x: number]: FileType } = {};

      for (const answer of answersOld) {
        const newAnswer = answers.find(a => a.answerId === answer.answerId);
        if (newAnswer) {
          const image = await getFilesByFileOwnerId(
            answer.quizQuestionAnswerId.toString()
          );

          if (image?.tempUri) {
            previewsMap[newAnswer.quizQuestionAnswerId] = image.tempUri;
            filesMap[newAnswer.quizQuestionAnswerId] = image;
          }
        }
      }

      setPreviews(previewsMap);
    };

    fetchExistingImages();
  }, [answers, answersOld, open]);

  useEffect(() => {
    return () => {
      Object.values(previews).forEach(url => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previews]);
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {translate("screens_quiz_labels_send_imgAnswers")}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Progress Indicator at the top */}
      {Object.keys(isUploading).length > 0 && (
        <Box sx={{ px: 3, pt: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            {/* {translate("screens_quiz_labels_upload_progress")} */}
            Progresso
          </Typography>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            // sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
            {overallProgress}%
          </Typography>
        </Box>
      )}

      <DialogContent dividers>
        <Form onSubmit={handleSubmit}>
          {answers.map(answer => (
            <Box key={answer.quizQuestionAnswerId} mb={3}>
              <Typography variant="h6">
                {translate("screens_quiz_labels_answer_selectImg")} {answer.desc}
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={8}>
                  <Form.Group
                    controlId={`formFile-${answer.quizQuestionAnswerId}`}
                  >
                    <Form.Control
                      type="file"
                      placeholder={translate("screens_news_placeholders_image")}
                      onChange={event =>
                        handleFileChange(event, answer.quizQuestionAnswerId)
                      }
                      accept=".png, .jpg, .jpeg"
                    />
                  </Form.Group>
                </Grid>
                <Grid item xs={4}>
                  {previews[answer.quizQuestionAnswerId] && (
                    <Image
                      src={previews[answer.quizQuestionAnswerId] || ''}
                      alt={`Preview of answer ${answer.desc}`}
                      thumbnail
                      width={150}
                      height={150}
                    />
                  )}
                </Grid>
              </Grid>
              {isUploading[answer.quizQuestionAnswerId] && (
                <Box mt={2}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress[answer.quizQuestionAnswerId] || 0}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {uploadProgress[answer.quizQuestionAnswerId] || 0}%
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {translate("buttons_cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            Object.keys(isUploading).length > 0 ||
            !hasNewImages
          }
        >
          {Object.keys(isUploading).length > 0 ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            </>
          ) : (
            translate("buttons_send")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAddImageAnswer;
